import React, { useState } from 'react';
import axios from 'axios';

import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography,
    Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Snackbar, Alert,
    InputAdornment, Chip, Link, List, ListItem, ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlagiarismIcon from '@mui/icons-material/Plagiarism'; // <-- ÍCONO AÑADIDO

// Interfaz para la respuesta detallada de Click Info
interface ClickInfoResult {
    click_id: string;
    external_id?: string;
    click_timestamp: string;
    ip_address: string;
    user_agent: string;
    sub_id1?: string;
    sub_id2?: string;
    sub_id3?: string;
    sub_id4?: string;
    sub_id5?: string;
    sub_id6?: string;
    sub_id7?: string;
    sub_id8?: string;
    sub_id9?: string;
    sub_id10?: string;
    click_cost: string | number;
    is_converted: boolean;
    campaign_id: number;
    campaign_name?: string;
    offer_id: number;
    offer_name?: string;
    offer_url?: string;
    offer_default_payout?: string | number;
    offer_params_mapping?: { [key: string]: string } | null;
    traffic_source_id?: number;
    traffic_source_name?: string;
    traffic_source_postback_url?: string;
    // Las propiedades 'traffic_source_postback_template' y 'traffic_source_incoming_map'
    // han sido eliminadas de la interfaz ya que no se devuelven desde el backend.
    cpa_network_id?: number;
    cpa_network_name?: string;
    conversion_id?: number;
    conversion_timestamp?: string;
    conversion_payout?: string | number;
    conversion_revenue?: string | number;
    conversion_status?: string;
    transaction_id?: string;
    conversion_sub_id1?: string;
    conversion_sub_id2?: string;
}

const ClickInfoPage: React.FC = () => {
    const [clickId, setClickId] = useState<string>('');
    const [clickInfoResult, setClickInfoResult] = useState<ClickInfoResult | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const handleLookup = async (event: React.FormEvent) => {
        event.preventDefault();
        setIsLoading(true);
        setClickInfoResult(null);
        setNotification(null);

        if (!clickId.trim()) {
            setNotification({ type: 'error', message: 'Por favor, introduce un Click ID para buscar.' });
            setIsLoading(false);
            return;
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/click-info/${clickId}`);
            setClickInfoResult(response.data);
            setNotification({ type: 'success', message: 'Búsqueda exitosa!' });
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Error desconocido durante la búsqueda.';
            setNotification({ type: 'error', message: `Error: ${errorMessage}` });
            console.error("Error al buscar Click Info:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper para renderizar valores de forma consistente
    const renderValue = (value: any, type?: 'date' | 'url' | 'json' | 'currency' | 'boolean'): React.ReactNode => {
        if (value === null || value === undefined || value === '') {
            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
        }

        switch (type) {
            case 'date':
                return <Typography variant="body2">{new Date(value).toLocaleString('es-MX')}</Typography>;
            case 'url':
                return (
                    <Link href={value} target="_blank" rel="noopener noreferrer" variant="body2">
                        {value.length > 50 ? `${value.substring(0, 47)}...` : value}
                    </Link>
                );
            case 'json':
                // Para JSON, intentar parsear si es un string y luego stringify para pretty print
                let jsonContent = value;
                if (typeof value === 'string') {
                    try {
                        jsonContent = JSON.parse(value);
                    } catch (e) {
                        // Si no es un JSON válido, se muestra como texto plano
                        jsonContent = value;
                    }
                }
                return <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', backgroundColor: 'rgba(0, 0, 0, 0.05)', p: 1, borderRadius: '4px' }}>{JSON.stringify(jsonContent, null, 2)}</Typography>;
            case 'currency':
                return <Typography variant="body2">${parseFloat(String(value)).toFixed(2)}</Typography>;
            case 'boolean':
                return <Chip label={value ? 'Sí' : 'No'} color={value ? 'success' : 'default'} size="small" />;
            default:
                return <Typography variant="body2">{String(value)}</Typography>;
        }
    };

    // Helper para crear ListItem para cada SubID (aunque no se usa directamente en la tabla, se mantiene para referencia si se usara List en otro lugar)
    const renderSubId = (label: string, value: string | undefined) => {
        if (!value) return null;
        return (
            <ListItem disablePadding dense>
                <ListItemText primary={label} secondary={renderValue(value)} sx={{ m: 0 }} />
            </ListItem>
        );
    };


    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Snackbar para notificaciones */}
            <Snackbar
                open={!!notification}
                autoHideDuration={6000}
                onClose={() => setNotification(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>
                    {notification?.message}
                </Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <PlagiarismIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Buscador de Información de Click
            </Typography>

            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                    Buscar Click
                </Typography>
                <Box component="form" onSubmit={handleLookup} noValidate>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            label="Introduce el Click ID"
                            id="click_id_input"
                            value={clickId}
                            onChange={(e) => setClickId(e.target.value)}
                            placeholder="ej. FINAL_CLICK_001"
                            required
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button type="submit" variant="contained" disabled={isLoading} sx={{ minWidth: { xs: '100%', sm: '150px' }, height: '56px' }}>
                            {isLoading ? <CircularProgress size={24} /> : 'Buscar Click'}
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            {clickInfoResult && (
                <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                    <Typography variant="h6" component="h2" gutterBottom color="text.primary">
                        Detalles del Click: {clickInfoResult.click_id}
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                {/* Información General del Click */}
                                <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">Información General</TableCell></TableRow>
                                <TableRow><TableCell component="th" width="30%">ID de Clic:</TableCell><TableCell>{renderValue(clickInfoResult.click_id)}</TableCell></TableRow>
                                {clickInfoResult.external_id && <TableRow><TableCell component="th">ID Externo:</TableCell><TableCell>{renderValue(clickInfoResult.external_id)}</TableCell></TableRow>}
                                <TableRow><TableCell component="th">Timestamp Click:</TableCell><TableCell>{renderValue(clickInfoResult.click_timestamp, 'date')}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Dirección IP:</TableCell><TableCell>{renderValue(clickInfoResult.ip_address)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">User Agent:</TableCell><TableCell>{renderValue(clickInfoResult.user_agent)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Costo Click:</TableCell><TableCell>{renderValue(clickInfoResult.click_cost, 'currency')}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Convertido:</TableCell><TableCell>{renderValue(clickInfoResult.is_converted, 'boolean')}</TableCell></TableRow>
                                
                                {/* Información de SubIDs */}
                                {(clickInfoResult.sub_id1 || clickInfoResult.sub_id2 || clickInfoResult.sub_id3 || clickInfoResult.sub_id4 || clickInfoResult.sub_id5 || clickInfoResult.sub_id6 || clickInfoResult.sub_id7 || clickInfoResult.sub_id8 || clickInfoResult.sub_id9 || clickInfoResult.sub_id10) && (
                                    <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">SubIDs</TableCell></TableRow>
                                )}
                                {clickInfoResult.sub_id1 && <TableRow><TableCell component="th">SubID 1:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id1)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id2 && <TableRow><TableCell component="th">SubID 2:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id2)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id3 && <TableRow><TableCell component="th">SubID 3:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id3)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id4 && <TableRow><TableCell component="th">SubID 4:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id4)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id5 && <TableRow><TableCell component="th">SubID 5:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id5)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id6 && <TableRow><TableCell component="th">SubID 6:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id6)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id7 && <TableRow><TableCell component="th">SubID 7:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id7)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id8 && <TableRow><TableCell component="th">SubID 8:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id8)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id9 && <TableRow><TableCell component="th">SubID 9:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id9)}</TableCell></TableRow>}
                                {clickInfoResult.sub_id10 && <TableRow><TableCell component="th">SubID 10:</TableCell><TableCell>{renderValue(clickInfoResult.sub_id10)}</TableCell></TableRow>}

                                {/* Información de Campaña */}
                                <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">Campaña</TableCell></TableRow>
                                <TableRow><TableCell component="th">ID Campaña:</TableCell><TableCell>{renderValue(clickInfoResult.campaign_id)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Nombre Campaña:</TableCell><TableCell>{renderValue(clickInfoResult.campaign_name)}</TableCell></TableRow>

                                {/* Información de Oferta */}
                                <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">Oferta</TableCell></TableRow>
                                <TableRow><TableCell component="th">ID Oferta:</TableCell><TableCell>{renderValue(clickInfoResult.offer_id)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Nombre Oferta:</TableCell><TableCell>{renderValue(clickInfoResult.offer_name)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">URL Oferta:</TableCell><TableCell>{renderValue(clickInfoResult.offer_url, 'url')}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Payout Pred. Oferta:</TableCell><TableCell>{renderValue(clickInfoResult.offer_default_payout, 'currency')}</TableCell></TableRow>
                                {clickInfoResult.offer_params_mapping && <TableRow><TableCell component="th">Mapeo Parámetros Oferta:</TableCell><TableCell>{renderValue(clickInfoResult.offer_params_mapping, 'json')}</TableCell></TableRow>}

                                {/* Información de Fuente de Tráfico */}
                                <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">Fuente de Tráfico</TableCell></TableRow>
                                <TableRow><TableCell component="th">ID Fuente Tráfico:</TableCell><TableCell>{renderValue(clickInfoResult.traffic_source_id)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Nombre Fuente Tráfico:</TableCell><TableCell>{renderValue(clickInfoResult.traffic_source_name)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">URL Postback Fuente:</TableCell><TableCell>{renderValue(clickInfoResult.traffic_source_postback_url, 'url')}</TableCell></TableRow>
                                {/* Las propiedades 'traffic_source_postback_template' y 'traffic_source_incoming_map'
                                    fueron eliminadas de la interfaz y su renderizado, ya que no se devuelven desde el backend. */}

                                {/* Información de Red CPA */}
                                <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">Red CPA</TableCell></TableRow>
                                <TableRow><TableCell component="th">ID Red CPA:</TableCell><TableCell>{renderValue(clickInfoResult.cpa_network_id)}</TableCell></TableRow>
                                <TableRow><TableCell component="th">Nombre Red CPA:</TableCell><TableCell>{renderValue(clickInfoResult.cpa_network_name)}</TableCell></TableRow>

                                {/* Información de Conversión (si existe) */}
                                {clickInfoResult.conversion_id && (
                                    <>
                                        <TableRow sx={{ '& > th': { fontWeight: 'bold', backgroundColor: 'action.hover' } }}><TableCell component="th" colSpan={2} align="center">Detalles de Conversión</TableCell></TableRow>
                                        <TableRow><TableCell component="th">ID Conversión:</TableCell><TableCell>{renderValue(clickInfoResult.conversion_id)}</TableCell></TableRow>
                                        <TableRow><TableCell component="th">Timestamp Conversión:</TableCell><TableCell>{renderValue(clickInfoResult.conversion_timestamp, 'date')}</TableCell></TableRow>
                                        <TableRow><TableCell component="th">Payout Conversión:</TableCell><TableCell>{renderValue(clickInfoResult.conversion_payout, 'currency')}</TableCell></TableRow>
                                        <TableRow><TableCell component="th">Estado Conversión:</TableCell><TableCell>
                                            <Chip
                                                label={clickInfoResult.conversion_status || 'N/A'}
                                                color={clickInfoResult.conversion_status === 'approved' ? 'success' : clickInfoResult.conversion_status === 'pending' ? 'info' : 'default'}
                                                size="small"
                                            />
                                        </TableCell></TableRow>
                                        <TableRow><TableCell component="th">ID Transacción:</TableCell><TableCell>{renderValue(clickInfoResult.transaction_id)}</TableCell></TableRow>
                                        {clickInfoResult.conversion_sub_id1 && <TableRow><TableCell component="th">Conv. SubID 1:</TableCell><TableCell>{renderValue(clickInfoResult.conversion_sub_id1)}</TableCell></TableRow>}
                                        {clickInfoResult.conversion_sub_id2 && <TableRow><TableCell component="th">Conv. SubID 2:</TableCell><TableCell>{renderValue(clickInfoResult.conversion_sub_id2)}</TableCell></TableRow>}
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Container>
    );
};

export default ClickInfoPage;